import { useEffect, useRef, useState } from "react";
import "../../styles/Home.css";
import { Link } from "react-router-dom";

import Slideshow from "../components/Slideshow";
import hero640 from "../../assets/slideshow/Banner_Image1-640.webp";
import hero960 from "../../assets/slideshow/Banner_Image1-960.webp";
import hero1280 from "../../assets/slideshow/Banner_Image1-1280.webp";
import schoolImage from "../../assets/School_image2.webp";

import Train from "../components/Train";
import Counter from "../components/Counter";
import AcademicsOverview from "../components/AcademicsOverview";
import Gallery from "./Gallery";
import ContactUs from "./ContactUs";
import Notices from "../components/Notices";
import useSEO from "../../hooks/useSeo";

function importAll(r) {
  return r.keys().map(r);
}

const aboutImages = importAll(
  require.context("../../assets/Images/gallery", false, /-300\.webp$/),
).sort();

export default function Home() {
  useSEO({
    title: "Priyadharshinie Public School | Quality Education",
    description:
      "Priyadharshinie Public School provides quality education with experienced teachers, modern facilities, and a supportive learning environment.",
    url: "https://www.priyadharshiniepublicschool.in/",
  });

  const aboutRef = useRef(null);
  const [startCount, setStartCount] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setStartCount(true),
      { threshold: 0.35 },
    );
    if (aboutRef.current) observer.observe(aboutRef.current);
    return () => observer.disconnect();
  }, []);

  const [aboutImg1, aboutImg2, aboutImg3] = aboutImages;

  return (
    <div className="home-container">
      {/* ================= HERO (LCP) ================= */}
      <section className="hero-section slideshow-container">
        <img
          src={hero960}
          srcSet={`${hero640} 640w, ${hero960} 960w, ${hero1280} 1280w`}
          sizes="100vw"
          alt="Priyadharshinie Public School Campus"
          className="hero-lcp-image"
          width="1280"
          height="720"
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          onLoad={() => setHeroLoaded(true)}
        />

        <div className="slide-overlay" />

        <div className="hero-overlay">
          <h1>Priyadharshinie Public School</h1>
          <p className="hero-subtitle">
            An English Medium School nurturing confident learners through
            quality education, strong values, and holistic development.
          </p>
          <p className="hero-meta">
            📍 <strong>Chikkasandra, Bengaluru</strong> • Pre-KG to X
          </p>
          <div className="hero-cta">
            <Link to="/admission" className="primary-btn">
              Apply for Admission
            </Link>
            <Link to="/contact-us" className="secondary-btn">
              Schedule a Visit
            </Link>
          </div>
        </div>

        {/* Slideshow loads ONLY after LCP */}
        {heroLoaded && <Slideshow />}
      </section>

      {/* ================= OBJECTIVE ================= */}
      <section className="objective-section">
        <div className="objective-img-wrapper">
          <img
            src={schoolImage}
            alt="School Campus"
            width="550"
            height="380"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="objective-content">
          <h2>Our Objective</h2>
          <p>
            Our school is dedicated to fostering an environment where every
            student is encouraged to grow academically, socially, and
            emotionally.
          </p>
          <p>
            With experienced educators and modern facilities, we empower
            students to become confident, responsible individuals.
          </p>
        </div>
      </section>

      <Notices />
      <div className="train-wrapper">
        <Train />
      </div>

      <section className="admission-section">
        <h2>Enroll your child today and give the best start to their future</h2>
        <Link to="/admission" className="admission-btn">
          Apply for Admission
        </Link>
      </section>

      <section className="home-about-section" ref={aboutRef}>
        <div className="home-about-left">
          <h2>About Our School</h2>

          <p>
            Priyadharshinie Public School is committed to nurturing young minds
            through quality education, strong values, and a supportive learning
            environment. For over two decades, we have focused on academic
            excellence while encouraging confidence, creativity, and discipline
            in every child.
          </p>

          <div className="home-stats-grid">
            <div className="home-stat-card">
              <h3>{startCount && <Counter target={20} />}+</h3>
              <p>Years of Excellence</p>
            </div>

            <div className="home-stat-card">
              <h3>{startCount && <Counter target={98} />}%</h3>
              <p>Academic Success</p>
            </div>

            <div className="home-stat-card">
              <h3>{startCount && <Counter target={1000} />}+</h3>
              <p>Happy Students</p>
            </div>

            <div className="home-stat-card">
              <h3>{startCount && <Counter target={30} />}+</h3>
              <p>Dedicated Teachers</p>
            </div>
          </div>

          <Link to="/about" className="home-about-btn">
            Know More →
          </Link>
        </div>

        <div className="home-about-right">
          <div className="home-about-grid">
            {/* Main highlight image */}
            <img
              src={aboutImg3}
              alt="Students participating in school activities at Priyadharshinie Public School"
              className="wide"
              loading="lazy"
            />

            {/* Supporting images */}
            <img
              src={aboutImg1}
              alt="Group activities and student engagement at school"
              loading="lazy"
            />

            <img
              src={aboutImg2}
              alt="School events and collaborative learning environment"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <AcademicsOverview />
      <Gallery limit={12} />
      <ContactUs />
    </div>
  );
}
